import BigNumber from "bignumber.js";
import { BORDER_RADIUS, COLOR, FONT_SIZE } from "constants/styles";
import React from "react";
import { numberFormatter } from "utils/formatting";

interface Props extends Omit<React.HTMLAttributes<HTMLDivElement>, "resource"> {
  used?: number | string;
  quota: number | string;
  resource: {
    name:
      | "project"
      | "webhook"
      | "request"
      | "member"
      | "MB of IPFS"
      | "MB of Filecoin";
    uncountable?: boolean;
  };
  boosted?: boolean;
  formatter?: (num: number | string) => number;
  "data-testid"?: string;
}

const getBarPercentage = (
  used: Props["used"] | undefined,
  quota: Props["quota"]
) => {
  if (!used) return 0;

  const factor =
    typeof used === "string" || typeof quota === "string"
      ? new BigNumber(used).div(quota).toNumber()
      : used / quota;

  const width = Math.floor(factor * 100);
  return width === 0 ? 1 : width;
};

const getBarColor = (widthPercent: number, boosted?: boolean) => {
  const overTreshold = widthPercent >= 80;

  if (boosted) {
    return {
      outer: `linear-gradient(
        90deg,
        ${COLOR.BG_PURPLE} 0%,
        #a2daff 100%
      )`,
      inner: "#0094f7",
    };
  }

  if (overTreshold) {
    return {
      outer: "#ffd791",
      inner: "#f9a81d",
    };
  }

  return {
    outer: "#a2daff",
    inner: "#0094f7",
  };
};

const RESOURCE_LABEL = {
  project: "Projects",
  webhook: "Webhooks",
  request: "Requests",
  member: "Workspace members",
  "MB of IPFS": "IPFS",
  "MB of Filecoin": "Filecoin",
};

const QuotaUsageBar = ({
  resource,
  quota,
  used,
  formatter,
  boosted,
  ...rest
}: Props) => {
  const barWidthPercent = getBarPercentage(used, quota);

  const barColors = getBarColor(barWidthPercent, boosted);
  return (
    <>
      <div className="quota-item" {...rest}>
        <div className="quota-label">{RESOURCE_LABEL[resource.name]}</div>
        <div
          className={`quota-bar-outer ${boosted ? "animated-gradient" : ""}`}
        >
          <div
            className="quota-bar-inner"
            style={{
              width: `${barWidthPercent}%`,
            }}
          ></div>
        </div>
        <div
          className="quota-bar-label"
          data-testid={`${rest["data-testid"]}-label`}
        >
          {used !== undefined && (
            <span className="quota-value">
              {numberFormatter(used, formatter)}
              <span>&nbsp;out of&nbsp;</span>
            </span>
          )}
          <span>
            <span className="quota-value">
              {numberFormatter(quota, formatter)}
            </span>
            <span>&nbsp;</span>
            <span>
              {new BigNumber(quota).gt(1) && !resource.uncountable
                ? `${resource.name}s`
                : resource.name}
            </span>
          </span>
          {used === undefined && <span> remaining</span>}
          {boosted && <> (boosted)</>}
        </div>
      </div>

      <style jsx>{`
        .quota-item {
          display: flex;
          flex-direction: column;
        }

        .quota-label {
          color: ${COLOR.TEXT_PRIMARY};
          font-size: ${FONT_SIZE.XS};
          font-weight: 500;
          text-transform: uppercase;
          margin-bottom: 4px;
          letter-spacing: 0.5px;
        }

        .quota-value {
          font-size: ${FONT_SIZE.PRIMARY};
          font-weight: 400;
        }

        .quota-bar-outer {
          position: relative;
          /* width: 120px; */
          height: 18px;
          background: ${barColors.outer};
          border-radius: ${BORDER_RADIUS.SM};
          overflow: hidden;
        }

        .quota-bar-inner {
          background: ${barColors.inner};
          height: 18px;
        }

        .quota-bar-label {
          font-size: ${FONT_SIZE.PRIMARY};
          font-weight: 400;
          margin-top: 4px;
          /* margin-left: 1ch; */
        }

        .animated-gradient {
          background-size: 600%;
          animation: gradient-animation 6s linear infinite;
        }

        @keyframes gradient-animation {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
      `}</style>
    </>
  );
};

export default QuotaUsageBar;
